import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import type { EnrichedFixture, UserSnapshot } from "../../shared/types";
import { ownerNames } from "../../shared/owners";
import { ownerColor as OWNER_COLORS } from "../../shared/colors";

type Metric =
  | "teamsRemaining"
  | "goalDifference"
  | "goalsFor"
  | "points"
  | "rank";

type Props = {
  title: string;
  metric: Metric;
  history: UserSnapshot[];
  visibleOwners: Set<string>;
  reverseY?: boolean;
  fixtures: EnrichedFixture[];
};

export default function ChartCard({
  title,
  metric,
  history,
  visibleOwners,
  reverseY,
  fixtures,
}: Props) {
  const data = useMemo(() => {
    return history.map((snap) => {
      const point: Record<string, number | string> = {
        t: snap.timestamp,
        label: new Date(snap.timestamp).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      };
      for (const u of snap.users) {
        point[u.name] = u[metric];
      }
      return point;
    });
  }, [history, metric]);

  const annotations = useMemo(() => buildAnnotations(fixtures), [fixtures]);

  if (history.length === 0) {
    return (
      <div className="chart-card">
        <h3>{title}</h3>
        <div className="empty-card">Charts populate as matches finish.</div>
      </div>
    );
  }

  return (
    <div className="chart-card">
      <h3>{title}</h3>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 16, bottom: 20, left: 0 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              reversed={reverseY}
              domain={
                metric === "rank" ? [1, Math.max(ownerNames.length, 1)] : ["auto", "auto"]
              }
              allowDecimals={metric === "rank" ? false : true}
            />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {annotations.map((a) => (
              <ReferenceLine
                key={a.label + a.x}
                x={a.x}
                stroke="#94a3b8"
                strokeDasharray="2 4"
                label={{ value: a.label, fontSize: 10, fill: "#475569" }}
              />
            ))}
            {ownerNames
              .filter((o) => visibleOwners.has(o))
              .map((o) => (
                <Line
                  key={o}
                  type="monotone"
                  dataKey={o}
                  stroke={OWNER_COLORS[o] ?? "#0f172a"}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function buildAnnotations(fixtures: EnrichedFixture[]) {
  const stages: { stage: string; label: string }[] = [
    { stage: "Group stage", label: "Group stage ends" },
    { stage: "Round of 32", label: "Round of 32" },
    { stage: "Round of 16", label: "Round of 16" },
    { stage: "Quarter-finals", label: "Quarter-finals" },
    { stage: "Semi-finals", label: "Semi-finals" },
    { stage: "Final", label: "Final" },
  ];
  const out: { x: string; label: string }[] = [];
  for (const { stage, label } of stages) {
    const inStage = fixtures.filter((f) => f.stage === stage);
    if (!inStage.length) continue;
    const referenceDate =
      stage === "Group stage"
        ? [...inStage].sort(
            (a, b) =>
              new Date(b.dateUtc).getTime() - new Date(a.dateUtc).getTime()
          )[0]
        : [...inStage].sort(
            (a, b) =>
              new Date(a.dateUtc).getTime() - new Date(b.dateUtc).getTime()
          )[0];
    out.push({
      x: new Date(referenceDate.dateUtc).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
      label,
    });
  }
  return out;
}
